import { useMutation } from '@tanstack/react-query';
import { EmployeeFormType } from '@/types';
import { api } from '@/api';
import { queryClient } from '@/queries';
import { employeeQueries } from './employee.query';
import { userQueries } from '@/queries/user';
import { employeesQueryKeys } from '@/queries/employee/employee.keys';
import { usersQueryKeys } from '@/queries/user/user.keys';
import { Employee } from '@/api/apis/resources';

const updateEmployeeFactory = (request: (body: Partial<EmployeeFormType> & { id: string }) => Promise<Employee>) => {
  return (profileUserId?: string) => {
    return useMutation({
      mutationFn: ({ ...variables }: Partial<EmployeeFormType> & { id: string; homeLocation?: string }) =>
        request(variables),
      meta: {
        invalidates: [employeesQueryKeys.getList('users')],
      },
      onSuccess: (data, variables) => {
        if (data.userId === profileUserId) {
          queryClient.refetchQueries({ queryKey: userQueries.profile().queryKey });
        }
        queryClient.setQueryData(employeeQueries.item(variables.id).queryKey, data);
      },
    });
  };
};

export const useUpdateCSMutation = updateEmployeeFactory(api.resource.updateCSEmployee);
export const useUpdateCLMutation = updateEmployeeFactory(api.resource.updateCLEmployee);
export const useUpdateEmployeesMutation = updateEmployeeFactory(api.resource.updateEmployee);

export const useUpdateProfileMutation = () => {
  return useMutation({
    mutationFn: api.resource.updateProfile,
    meta: {
      refetch: [usersQueryKeys.getProfile()],
    },
  });
};

const setEmployeesActiveStateFactory = (request: (id: string) => Promise<Employee>) => {
  return () => {
    return useMutation({
      mutationFn: (id: string) => request(id),
      meta: {
        invalidates: [employeesQueryKeys.getList('users')],
      },
      onSuccess: (_, id) => {
        queryClient.setQueryData(employeeQueries.item(id).queryKey, _);
      },
    });
  };
};

export const useActivateEmployeeMutation = setEmployeesActiveStateFactory(api.resource.activateEmployee);
export const useActivateCSMutation = setEmployeesActiveStateFactory(api.resource.activateCSEmployee);
export const useActivateCLMutation = setEmployeesActiveStateFactory(api.resource.activateCLEmployee);
export const useDeactivateEmployeeMutation = setEmployeesActiveStateFactory(api.resource.deactivateEmployee);
export const useDeactivateCSMutation = setEmployeesActiveStateFactory(api.resource.deactivateCSEmployee);
export const useDeactivateCLMutation = setEmployeesActiveStateFactory(api.resource.deactivateCLEmployee);
